// Shared Google Maps API loader to prevent loading the API multiple times
// Adapted from fleet-manager apps/web for web-components package

// Global function name Google calls once the API and its libraries are ready.
// It has to be reachable from `window` because Google invokes it by name.
const READY_CALLBACK = "__fleetsyncGoogleMapsReady";

let isLoaded = false;
let isLoading = false;
let loadPromise: Promise<void> | null = null;

export const loadGoogleMapsAPI = (apiKey: string): Promise<void> => {
  // If already loaded, return resolved promise
  if (isLoaded) {
    return Promise.resolve();
  }

  // If currently loading, return the existing promise
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Start loading
  isLoading = true;
  loadPromise = new Promise((resolve, reject) => {
    // Check if already loaded (in case another component loaded it)
    if (window.google && window.google.maps) {
      isLoaded = true;
      isLoading = false;
      resolve();
      return;
    }

    if (!apiKey) {
      const error = new Error("Google Maps API key not provided");
      isLoading = false;
      reject(error);
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com"]',
    );
    if (existingScript) {
      // Script exists, wait for it to load
      const checkIfLoaded = () => {
        if (window.google && window.google.maps) {
          isLoaded = true;
          isLoading = false;
          resolve();
        } else {
          setTimeout(checkIfLoaded, 100);
        }
      };
      checkIfLoaded();
      return;
    }

    // Create and load the script.
    //
    // `loading=async` is what Google asks for, and it changes how readiness is
    // signalled: it "prevents JavaScript execution triggered by the script's
    // load event", so `script.onload` no longer means the API and its
    // libraries are usable. The bootstrap callback does, so the promise is
    // settled from there.
    const script = document.createElement("script");
    const globals = window as unknown as Record<string, unknown>;

    globals[READY_CALLBACK] = () => {
      delete globals[READY_CALLBACK];
      isLoaded = true;
      isLoading = false;
      resolve();
    };

    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly&loading=async&callback=${READY_CALLBACK}`;
    script.async = true;
    script.defer = true;

    script.onerror = () => {
      delete globals[READY_CALLBACK];
      const error = new Error("Failed to load Google Maps API");
      isLoading = false;
      reject(error);
    };

    document.head.appendChild(script);
  });

  return loadPromise;
};

export const isGoogleMapsLoaded = (): boolean => {
  return isLoaded && !!(window.google && window.google.maps);
};

// Declare global types
declare global {
  interface Window {
    google: typeof google;
  }
}
